'use client';

import { initRum } from '@curology/logger/client';
import {
  GTM_DATA_LAYER_SCRIPT,
  GTMScript,
  RemoveAmplitudeDeviceIdScript,
  useGtmKey,
} from '@curology/site-tools/client';
import { type Brand } from '@curology/ui-components-web/brand';
import { BrandProvider } from '@curology/ui-components-web/brand/context';
import { DeclareBrandVariables } from '@curology/ui-components-web/brand/DeclareBrandVariables';
import { ToastList } from '@curology/ui-components-web/react/Toast/ToastList';
import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import { Provider as JotaiProvider } from 'jotai';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { BrandStyles, ResetStyles } from 'radiance-ui';
import { primaryTheme } from 'radiance-ui/lib/constants';
import React, { useEffect, useRef, useState } from 'react';

import FlashMessages from '@/components/global/flashMessages';
import COOKIE_KEYS from '@/consts/cookieKeys';
import { useDecagonChatBot } from '@/hooks/useDecagonChatBot';
import { UserService } from '@/services/userService/client';
import { type AnalyticsPatientResource } from '@/types/metrics/analyticsPatient';
import { identifyUser } from '@/utils/analytics';
import { initLd, getContext } from '@/utils/featureFlags/client';
import { extractAnalyticsPatientData } from '@/utils/metrics';

import ReactQueryClientProvider from './ReactQueryClientProvider';

if (typeof window !== 'undefined') {
  // TODO: Init with site tool Amplitude Browser SDK implmentation once GTM collisions are handled

  initRum({
    env: process.env.NEXT_PUBLIC_APP_ENV,
    excludedActivityUrls: [
      /googletagmanager\.com/i,
      /analytics\.tiktok\.com/i,
      /webkit-masked-url/i,
      /ocuboost/i,
      /spotify/i,
      /cdn-cgi/i,
      /yimg/i,
    ],
  });
}

const MainContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

type Props = {
  children: React.ReactNode;
  brand: Brand;
};

const hasAuthenticatedSession = () => {
  if (typeof document === 'undefined') {
    return false;
  }

  return document.cookie.includes(`${COOKIE_KEYS.sAccessToken}=`);
};

type PatientFetchArgs = {
  brand: Brand;
  win: Window & typeof globalThis;
  onSuccess: (patient: AnalyticsPatientResource) => void;
};

const scheduleIdleTask = (
  win: Window & typeof globalThis,
  task: () => void,
  timeout: number,
) => {
  // Defer non-critical work until the browser has breathing room.
  if (typeof win.requestIdleCallback === 'function') {
    const handle = win.requestIdleCallback(
      () => {
        task();
      },
      { timeout },
    );

    return () => {
      if (typeof win.cancelIdleCallback === 'function') {
        win.cancelIdleCallback(handle);
      }
    };
  }

  const timeoutHandle = win.setTimeout(task, 0);

  return () => {
    win.clearTimeout(timeoutHandle);
  };
};

const schedulePatientFetch = ({ brand, win, onSuccess }: PatientFetchArgs) => {
  let cancelled = false;

  const fetchPatient = () => {
    UserService.getPatient(brand)
      .then((patient) => {
        if (cancelled || !patient) {
          return;
        }

        onSuccess(patient);
      })
      .catch((error) => {
        console.warn(
          '[RootClientContainer] Failed to fetch authenticated patient.',
          error,
        );
      });
  };

  const cancelIdleTask = scheduleIdleTask(
    win,
    () => {
      if (!cancelled) {
        fetchPatient();
      }
    },
    2000,
  );

  return () => {
    cancelled = true;
    cancelIdleTask();
  };
};

const pinterestStubScript = `
  (function () {
    if (typeof window === 'undefined' || window.pintrk) {
      return;
    }
    var pinterestStub = function () {
      pinterestStub.queue.push(Array.prototype.slice.call(arguments));
    };
    pinterestStub.queue = [];
    pinterestStub.version = '3.0';
    pinterestStub.push = pinterestStub;
    window.pintrk = pinterestStub;
  })();
`;

export const RootClientContainer = ({ children, brand }: Props) => {
  const pathname = usePathname();
  const [isMounted, setIsMounted] = useState(false);
  const hasFetchedPatient = useRef(false);

  const [rootLevelPatient, setRootLevelPatient] = useState<
    AnalyticsPatientResource | undefined
  >();

  useDecagonChatBot(brand, rootLevelPatient);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    getContext(brand)
      .then((context) => {
        initLd(context);
      })
      .catch((error) => {
        console.warn(
          '[RootClientContainer] LaunchDarkly initialization failed.',
          error,
        );
      });
  }, [brand]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (hasFetchedPatient.current || !hasAuthenticatedSession()) {
      return;
    }

    // Wait for the first frame before fetching patient data so LCP stays fast.
    return schedulePatientFetch({
      brand,
      win: window as Window & typeof globalThis,
      onSuccess: (patient) => {
        const analyticsPatient = extractAnalyticsPatientData(patient);
        identifyUser({ patient: analyticsPatient });
        setRootLevelPatient(patient);
        hasFetchedPatient.current = true;
      },
    });
  }, [brand, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const brandedProdGtmKeys: Record<Brand, string | undefined> = {
    curology: process.env.NEXT_PUBLIC_CUROLOGY_GTM_KEY,
    agency: process.env.NEXT_PUBLIC_AGENCY_GTM_KEY,
  };

  const gtmKey = useGtmKey({ prodKey: brandedProdGtmKeys[brand] });

  // TODO: Remove ThemeProvider, BrandStyles, and ResetStyles when radiance-ui is no longer used
  return (
    <ReactQueryClientProvider>
      <DeclareBrandVariables brand={brand} />
      <BrandProvider brand={brand}>
        <JotaiProvider>
          <ThemeProvider theme={primaryTheme}>
            <BrandStyles />
            <ResetStyles />
            <Script
              id="gtmDataLayer"
              type="text/javascript"
              strategy="beforeInteractive"
            >
              {GTM_DATA_LAYER_SCRIPT}
            </Script>
            <Script
              id="pinterestPixelStub"
              type="text/javascript"
              strategy="beforeInteractive"
            >
              {pinterestStubScript}
            </Script>
            <MainContainer>{children}</MainContainer>
            {isMounted && <ToastList className="cur-left-2 md:cur-left-auto" />}
            <FlashMessages />
            <GTMScript overrideKey={gtmKey} />
            <RemoveAmplitudeDeviceIdScript />
          </ThemeProvider>
        </JotaiProvider>
      </BrandProvider>
    </ReactQueryClientProvider>
  );
};
